import { Component, createRef } from 'preact';
import { Coffee, Swords, MessageSquare, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader, { RolePreviewButton } from './AppHeader';
import BetaBadge from './BetaBadge';
import ChangelogModal from './ChangelogModal';
import { fetchReleaseNotes } from '../utils/updateManager';
import { UI, playUI } from '../utils/arena/uiSounds';

const SUPPORT_URL = 'https://buymeacoffee.com/clutterfox';
import { xpProgressInLevel, hasAnyPendingClaim } from '../utils/arena/profileDefaults';
import { formatRank, TIER_COLORS, TIER_LABELS, TIERS } from '../utils/arena/rankUtils';
import {
  GOLD, BG_ATMOSPHERE,
  CONTENT_BG_DEFAULT, CONTENT_BG_HOVER, CONTENT_BG_ACTIVE,
  BTN_BORDER, BTN_BORDER_HOVER, BTN_CORNER, BTN_CORNER_HOVER,
  DIALOG_STYLE, DIALOG_BORDER, TEXT_PRIMARY, TEXT_MUTED, ACCENT_GOLD,
  GLASS_PANEL_STYLE, GOLD_BTN, RADIUS, innerRadius,
  adjustAlpha, CornerPlating, MenuButton, OrnamentalDivider, FourCorners,
} from '../lib/medievalTheme';
import { getCanvasScale, onCanvasScaleChange } from '../utils/uiScale';
import { EM_DASH } from '../lib/typography';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import { resolveProfileBackdrop } from '../utils/arena/profileBackdrop';
import { getHubProfileBg, onHubProfileBgChange } from '../utils/arena/hubBackground';
import { HeroBackdrop } from './profile/ProfileHero';
import AvatarImage from './ui/AvatarImage';
import RowArtBackdrop from './RowArtBackdrop';
import AmbientParticles from './AmbientParticles';
import AdminPanel from './AdminPanel';
import VikingOrnament from './VikingOrnament';
import VirtualList from './VirtualList';
import TutorialOverlay from './TutorialOverlay';
import { shouldAutoPlay, markTutorialSeen, hydrateTutorialState } from '../utils/arena/tutorialState';
import { Tooltip } from './ui/tooltip';
import { TabBar } from './ui/TabBar';
import { Select } from './ui/select';
import GlobalChatView from './GlobalChatView';
import * as lfg from '../utils/arena/lfgStore';
import * as onlinePlayers from '../utils/arena/onlinePlayersStore';
import ChallengeMenu from './ChallengeMenu';
import { IconEyeOpen, IconEyeClosed } from './ui/icons';
import LfgWidget from './LfgWidget';
import { PillTabs, ROW_HEIGHT_CQW, NATIVE_FONT_PX, PANEL_CONTROL_RADIUS } from './ui/PillTabs';
import CardInspector from './CardInspector';
import DiscordHandlePill from './ui/DiscordHandlePill';
import PlayerTitle from './PlayerTitle';
import PlayerBadgeStrip from './PlayerBadgeStrip';
import CreditsScreen from './CreditsScreen';
import { isDevBuild } from '../utils/devMode';
import { isDevServerBuild } from '../utils/updateChannel';
import { k, t } from '../localization/t';
import { setCurrentUserId as setCosmeticsUserId, getCosmeticsCached, awaitProtectedRegistry } from '../utils/arena/cosmeticsCache';

// Bumped to v3 alongside the new menu-button order
// (Play / Deck Builder / Store / Arcane Trials / Auction House) and
// the consolidated social hub button replacing the separate Mailbox
// and Friends entries. Bumping the key re-arms the auto-play for
// every existing player so they see the new walkthrough.
const HUB_TUTORIAL_KEY = 'hub-main-menu-v5';

// Persist the last-opened leaderboard tab so reopening the app drops
// the user back into whichever view (ranked / limited / sandbox) they
// were browsing last. Unknown / corrupt values fall back to ranked.
const LEADERBOARD_MODE_KEY = 'valkenhall:leaderboardMode';
const LEADERBOARD_MODES = ['ranked', 'limited', 'sandbox'];
function readLeaderboardMode() {
  try {
    const v = localStorage.getItem(LEADERBOARD_MODE_KEY);
    return LEADERBOARD_MODES.includes(v) ? v : 'ranked';
  } catch { return 'ranked'; }
}
function writeLeaderboardMode(mode) {
  try { localStorage.setItem(LEADERBOARD_MODE_KEY, mode); } catch {}
}

// Dev-only mock data used to visualise the leaderboard row styling when
// the real server returns <5 entries. Skipped entirely in packaged builds.
const MOCK_LEADERBOARD = [
  { id: 'm01', name: 'DragonsMaw',      tier: 'grandmaster', division: 1 },
  { id: 'm02', name: 'Stormweaver',     tier: 'grandmaster', division: 1 },
  { id: 'm03', name: 'NightVeil',       tier: 'master',      division: 1 },
  { id: 'm04', name: 'HexenBane',       tier: 'master',      division: 2 },
  { id: 'm05', name: 'LunaThorn',       tier: 'master',      division: 3 },
  { id: 'm06', name: 'AshenKing',       tier: 'expert',      division: 1 },
  { id: 'm07', name: 'ValeRunner',      tier: 'expert',      division: 2 },
  { id: 'm08', name: 'IronCrow',        tier: 'expert',      division: 3 },
  { id: 'm09', name: 'Duskfang',        tier: 'expert',      division: 4 },
  { id: 'm10', name: 'MirrorKite',      tier: 'adept',       division: 1 },
  { id: 'm11', name: 'SableWick',       tier: 'adept',       division: 2 },
  { id: 'm12', name: 'GravenOath',      tier: 'adept',       division: 3 },
  { id: 'm13', name: 'PaleWyvern',      tier: 'adept',       division: 4 },
  { id: 'm14', name: 'Hollowsong',      tier: 'journeyman',  division: 1 },
  { id: 'm15', name: 'Briarshade',      tier: 'journeyman',  division: 2 },
  { id: 'm16', name: 'CinderMorne',     tier: 'journeyman',  division: 3 },
  { id: 'm17', name: 'VesperMoth',      tier: 'journeyman',  division: 4 },
  { id: 'm18', name: 'FrostHollow',     tier: 'apprentice',  division: 1 },
  { id: 'm19', name: 'ThornVow',        tier: 'apprentice',  division: 2 },
  { id: 'm20', name: 'GlyphWarden',     tier: 'apprentice',  division: 3 },
];

// Ordered list of onboarding steps for the main menu. Each step
// points at a DOM node via a data-tutorial attribute declared on the
// target button. Keep the ordering roughly top-to-bottom so the
// spotlight doesn't jump around the screen.
const HUB_TUTORIAL_STEPS = [
  {
    key: 'play',
    title: k('Play'),
    body: k('Your gateway to every game mode: Ranked, Casual, Draft, Sandbox, and Solo matches all live behind this button.'),
    selector: '[data-tutorial="play"]',
  },
  {
    key: 'deck-builder',
    title: k('Deck Builder'),
    body: k('Browse your collection, build decks, and share them with other players.'),
    selector: '[data-tutorial="deck-builder"]',
  },
  {
    key: 'store',
    title: k('Store'),
    body: k('Spend gold on booster packs, craft singles with Arcana Shards, and pick up cosmetics like avatars, profile backgrounds, and playmats. Valkenhall is free-to-play: every currency is earned by playing, never bought with real money.'),
    selector: '[data-tutorial="store"]',
  },
  {
    key: 'arcane-trials',
    title: k('Arcane Trials'),
    body: k('The seasonal pass. Earn Season XP by playing matches and completing quests to unlock rewards.'),
    selector: '[data-tutorial="arcane-trials"]',
  },
  {
    key: 'auction-house',
    title: k('Auction House'),
    body: k('Buy and sell singles with other players. List cards for gold or bid on what you\'re hunting.'),
    selector: '[data-tutorial="auction-house"]',
  },
  {
    key: 'settings',
    title: k('Settings'),
    body: k('Audio, graphics, account, and the replay button for this tutorial all live here.'),
    selector: '[data-tutorial="settings"]',
  },
  {
    key: 'leaderboard',
    title: k('Hall'),
    body: k('Online shows everyone here right now: challenge them, chat, or add them as a friend. Leaderboard is the ladder.'),
    selector: '[data-tutorial="leaderboard"]',
  },
  {
    key: 'tavern',
    title: k('Tavern'),
    body: k('Global chat with everyone online. Mention a player with #name, link a card with @, reply to a message, or click an avatar to open their profile.'),
    selector: '[data-tutorial="tavern"]',
  },
  {
    key: 'profile-avatar',
    title: k('Your Profile'),
    body: k('Click your avatar to view your profile: stats, achievements (and claim their rewards), titles, decks you\'ve published for others, and a Showcase to flex your favorite cards.'),
    selector: '[data-tutorial="profile-avatar"]',
  },
  {
    key: 'music',
    title: k('Music & Ambience'),
    body: k('Control the soundtrack, set ambience, and play your own music library while you battle.'),
    selector: '[data-tutorial="music"]',
  },
  {
    key: 'mastery',
    title: k('Skill Tree'),
    body: k('Spend skill points across five mastery paths for permanent progression. A badge shows unspent points.'),
    selector: '[data-tutorial="skill-tree-header"]',
  },
  {
    key: 'social-hub',
    title: k('Social Hub'),
    body: k('Three tabs: Messages (friends and chat), Tavern (the global chat, from anywhere), and Notifications (match invites, activity, auction payouts, rewards and news). A badge appears when something needs you.'),
    selector: '[data-tutorial="social-hub"]',
  },
];

const TIER_HEX = {
  apprentice: '#a8a29e',
  journeyman: '#d4d4d8',
  adept: '#38bdf8',
  expert: '#a78bfa',
  master: '#fbbf24',
  grandmaster: '#f87171',
};

// Diagonal specular sheen from the top-left, like light catching the pane.
//
// Kept very faint on purpose. The grain already comes from the texture set
// in GLASS_PANEL_STYLE, so a second noise pass here would only double it, and at
// the old 0.1 alpha this sheen lifted the panel face to roughly [60,56,50]
// against the menu buttons' [27,25,21]. It, not the tint, was what stopped
// the panels matching the buttons.
function GlassSurface() {
  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden" style={{ borderRadius: 'var(--radius-xl)' }}>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(115deg, rgba(255, 245, 220, 0.028) 0%, rgba(255, 245, 220, 0.01) 28%, transparent 55%)' }} />
    </div>
  );
}


// Etched caption shared by the hero HUD (bar label, stat tile captions,
// collection footer). Always sans: the FantaisieArtistique display face
// is reserved for the player name and turns to mud below ~1cqw. Small
// text wants positive tracking. Never uppercase: title-case as written
// in the JSX (all-caps headings are banned app-wide).
const HUD_LABEL_STYLE = {
  fontSize: '0.625cqw',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textShadow: '0 1px 2px rgba(0,0,0,0.6)',
};

// Count-up for the hero stat tiles. Animates once on mount (hub entry is
// the occasional "moment" that earns it); later prop changes render
// immediately so live stat updates never lag behind the data.
class CountUpNumber extends Component {
  constructor(props) {
    super(props);
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.state = { display: !props.value || reduce ? props.value || 0 : 0 };
  }
  componentDidMount() {
    const { value } = this.props;
    if (!value || this.state.display === value) return;
    const start = performance.now();
    const DURATION = 700;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      const eased = 1 - Math.pow(1 - t, 3);
      this.setState({ display: Math.round(value * eased) });
      if (t < 1) this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      cancelAnimationFrame(this.raf);
      this.setState({ display: this.props.value || 0 });
    }
  }
  componentWillUnmount() {
    cancelAnimationFrame(this.raf);
  }
  render() {
    return this.state.display;
  }
}

// Leaderboard row text colors. One named pair instead of scattered hex
// literals, so the row and its title line can't drift apart.
const ROW_TEXT = '#d7d2ca';
const ROW_TEXT_SELF = '#f4d788';
// Same green as the friend-list presence dot (MiniProfile).
const ONLINE_DOT = '#6ac084';

// Small carved-tablet button used in the leaderboard header (Find Me /
// Season / Hall of Fame). Same recipe as the Arcane Trials
// TrialsLeaderboardButton (the sibling screen's version of this exact
// control), scaled down for the 1.875cqw pill height: solid stone-over-
// bronze fill, gold hairline inside the rim, inner vignette, warm outer
// glow that brightens on hover.
const HEADER_PILL_STYLE = {
  height: '1.875cqw',
  borderRadius: PANEL_CONTROL_RADIUS,
  // Translucent bronze rather than the Trials button's opaque fill:
  // the frosted panel face stays visible through the plate so the
  // control reads as a fitting on the panel, not a hole cut into it.
  background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, rgba(48,36,16,0.5) 0%, rgba(24,17,7,0.45) 100%)`,
  backgroundBlendMode: 'overlay, normal',
  border: `1px solid ${GOLD} 0.4)`,
  color: ACCENT_GOLD,
  fontSize: '0.625cqw',
  fontWeight: 700,
  boxShadow: [
    'inset 0 1px 0 rgba(255,235,195,0.09)',
    `inset 0 0 0 1px ${GOLD} 0.14)`,
    'inset 0 -2px 5px rgba(0,0,0,0.35)',
    'inset 0 0 14px rgba(0,0,0,0.3)',
    '0 2px 6px rgba(0,0,0,0.35)',
    `0 0 12px ${GOLD} 0.1)`,
  ].join(', '),
  textShadow: '0 1px 0 rgba(0,0,0,0.6), 0 0 8px rgba(212,168,67,0.2)',
  isolation: 'isolate',
};

const HEADER_PILL_HOVER_STYLE = {
  ...HEADER_PILL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, rgba(72,54,22,0.55) 0%, rgba(40,28,10,0.5) 100%)`,
  border: `1px solid ${GOLD} 0.65)`,
  boxShadow: [
    'inset 0 1px 0 rgba(255,235,195,0.14)',
    `inset 0 0 0 1px ${GOLD} 0.24)`,
    'inset 0 -2px 5px rgba(0,0,0,0.35)',
    'inset 0 0 14px rgba(0,0,0,0.28)',
    '0 2px 6px rgba(0,0,0,0.4)',
    `0 0 16px ${GOLD} 0.18)`,
  ].join(', '),
};

// `hint` goes through Tooltip, not a native `title`: main.jsx strips every
// title attribute on insert and on mutation, so a title= here renders
// nothing at all.
//
// `disabled` dims the pill in place instead of unmounting it, so pills
// whose availability depends on async data (Find Me needs the /me rank
// fetch) hold their layout slot from first paint rather than popping in
// when the response lands. Disabled buttons fire no mouse events, so the
// hover handlers never run and the Tooltip can't open. No extra guards
// needed beyond the dimmed style.
// `active` holds the hover (lit) look while a toggle pill is on.
// `height` overrides the filter-row pill height for the taller top row,
// where the pill sits beside the looking-for-a-match widget.
function HeaderPill({ onClick, hint, disabled, active, children, radius, height }) {
  const sizing = { ...(radius ? { borderRadius: radius } : {}), ...(height ? { height } : {}) };
  const restStyle = { ...(active ? HEADER_PILL_HOVER_STYLE : HEADER_PILL_STYLE), ...sizing };
  const button = (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 transition-[background,border-color,box-shadow,scale,opacity] ${disabled ? '' : 'active:scale-[0.96] cursor-pointer'}`}
      style={disabled
        ? { ...HEADER_PILL_STYLE, ...sizing, opacity: 0.45, cursor: "url('/cursors/default.png') 4 2, not-allowed" }
        : restStyle}
      onMouseEnter={(e) => Object.assign(e.currentTarget.style, HEADER_PILL_HOVER_STYLE, sizing)}
      onMouseLeave={(e) => Object.assign(e.currentTarget.style, restStyle)}
    >
      {children}
    </button>
  );
  if (!hint || disabled) return button;
  return <Tooltip content={hint} side="bottom" delay={350} maxWidth="18cqw">{button}</Tooltip>;
}

// Gold / silver / bronze for the top 3 rank numerals. Anything past 3rd
// is drawn in the muted numeral color instead.
const RANK_MEDAL_COLORS = {
  1: '#d4a843',
  2: '#c0c0c8',
  3: '#c08854',
};

// Skeleton row for the leaderboard. Mirrors the proportions of a real
// row so the layout doesn't jump when the data arrives, and uses the
// shared `.skeleton-block` shimmer animation defined in index.css so
// it visibly pulses rather than looking like an empty list. `variant`
// controls the rank-side ornament: 'rank' draws the real RankNumeral
// when the absolute rank is known (in-scroll placeholders for rows
// we haven't fetched yet), 'pip' reserves the same column width with
// an empty spacer for the initial pre-data load, when no row is
// anchored to a specific rank yet. Both draw a circle shimmer where
// the avatar will land, and both keep that circle at the same x.
function LeaderboardSkeletonRow({ rank = null, variant = 'rank' }) {
  // Slight random width jitter so a stack of skeleton rows reads as a
  // ladder of varying-length names rather than identical bars. Seeded
  // per-rank so each row is stable across renders.
  const seed = rank ?? Math.floor(Math.random() * 1000);
  const nameWidth = 32 + (seed * 13) % 28; // 32–60%
  const subWidth = 16 + (seed * 7) % 16;   // 16–32%
  return (
    <div
      className="relative flex items-center gap-3 pl-0 pr-3 py-2.5 rounded-md w-full h-full"
      aria-hidden="true"
    >
      {variant === 'rank' && rank != null
        ? <RankNumeral rank={rank} />
        : <span className="shrink-0" style={{ minWidth: RANK_COLUMN_WIDTH }} />}
      <div
        className="skeleton-block skeleton-circle shrink-0"
        style={{ width: '2.5cqw', height: '2.5cqw' }}
      />
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="skeleton-block" style={{ height: '0.875cqw', width: `${nameWidth}%` }} />
        <div className="skeleton-block" style={{ height: '0.55cqw', width: `${subWidth}%` }} />
      </div>
      <div className="skeleton-block shrink-0" style={{ height: '0.7cqw', width: '4.2cqw' }} />
    </div>
  );
}

// Width of the rank column. Shared with the skeleton row so a
// placeholder and a real row put the avatar in the same place. The
// list must not shift sideways when the first page lands.
const RANK_COLUMN_WIDTH = '3.25cqw';

// Leaderboard row height in design px (VirtualList needs px, not cqw).
// 52px = 3.25cqw on the 1600 design canvas, matching HallPlayerRow so
// the Players and Leaderboard tabs share the same row height.
const LEADERBOARD_ROW_PX = 52;

// Chrome-free rank column: a plain centered numeral. Top 3 carry
// their medal color (mid tone from RANK_MEDAL_COLORS); everyone else
// is muted. minWidth keeps the avatar column aligned up to 4 digits.
function RankNumeral({ rank }) {
  const medal = RANK_MEDAL_COLORS[rank];
  return (
    <span
      className="shrink-0 text-center font-mono font-bold tabular-nums"
      style={{
        minWidth: RANK_COLUMN_WIDTH,
        fontSize: medal ? '0.8125cqw' : '0.6875cqw',
        color: medal || 'rgba(166,160,155,0.75)',
        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
      }}
    >
      {rank}
    </span>
  );
}

// Round profile-avatar for a leaderboard row; falls back to an initial
// disc when the player has no avatar set (same recipe as the friends
// sidebar / chat list).
function RowAvatar({ player, sorceryCards }) {
  const url = resolveAvatarUrl(player, sorceryCards);
  if (url) {
    return (
      <AvatarImage
        src={url}
        className="shrink-0 rounded-full"
        style={{ width: '2.5cqw', height: '2.5cqw', border: `1px solid ${GOLD} 0.2)` }}
      />
    );
  }
  return (
    <div
      className="shrink-0 rounded-full flex items-center justify-center font-bold"
      style={{ width: '2.5cqw', height: '2.5cqw', fontSize: '1cqw', background: `${GOLD} 0.1)`, border: `1px solid ${GOLD} 0.2)`, color: TEXT_PRIMARY }}
    >
      {(player?.name || '?')[0].toUpperCase()}
    </div>
  );
}

// The hub glass panels round at --radius-2xl. One inset from the edge on
// every side, the codebase's panel-corner inset; every control touching
// that inset rounds at the panel radius minus it (lg), and pills one step
// further in (md).
const HALL_PAD = '0.5cqw';
const HALL_RADIUS = innerRadius(RADIUS['2xl'], HALL_PAD);
const HALL_WIDGET_HEIGHT = '2.75cqw';

const HALL_TABS = [
  { value: 'players', label: k('Online') },
  { value: 'leaderboard', label: k('Leaderboard') },
];
const PLAYER_FILTERS = [
  { value: 'all', label: k('All') },
  { value: 'looking', label: k('Looking') },
  { value: 'friends', label: k('Friends') },
  { value: 'inmatch', label: k('In a match') },
];

const WELL_SHADOW = 'inset 0 2px 7px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(212,168,67,0.07), inset 0 0 0 1px rgba(0,0,0,0.25), 0 1px 0 rgba(212,168,67,0.05)';

// Recessed search well. The input renders at a fixed native size and is
// scaled with the canvas, same trick as the pill labels.
function SearchWell({ value, onInput, placeholder = t('Search…'), width = '12cqw', radius = PANEL_CONTROL_RADIUS }) {
  return (
    <div
      className="relative overflow-hidden transition-[border-color,box-shadow] duration-150 shrink-0"
      style={{
        width,
        height: ROW_HEIGHT_CQW,
        borderRadius: radius,
        background: 'url("/tex-noise.webp"), linear-gradient(180deg, rgba(4,3,2,0.55) 0%, rgba(10,8,4,0.45) 100%)',
        border: `1px solid ${GOLD} 0.2)`,
        boxShadow: WELL_SHADOW,
      }}
    >
      <svg
        className="absolute pointer-events-none"
        style={{ left: '0.5cqw', top: '50%', transform: 'translateY(-50%)', width: '0.875cqw', height: '0.875cqw', color: `${GOLD} 0.5)` }}
        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 'calc(100% / var(--canvas-scale, 1))',
          height: 'calc(100% / var(--canvas-scale, 1))',
          transform: 'scale(var(--canvas-scale, 1))',
          transformOrigin: 'top left',
          fontSize: `${NATIVE_FONT_PX}px`,
          padding: '0 8px 0 28px',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: '#A6A09B',
          caretColor: ACCENT_GOLD,
        }}
        onInput={(e) => onInput(e.target.value)}
        onFocus={(e) => {
          const well = e.currentTarget.parentElement;
          well.style.borderColor = `${GOLD} 0.45)`;
          well.style.boxShadow = `${WELL_SHADOW}, inset 0 1px 0 rgba(255,220,140,0.07), 0 0 12px rgba(212,168,67,0.12)`;
        }}
        onBlur={(e) => {
          const well = e.currentTarget.parentElement;
          well.style.borderColor = `${GOLD} 0.2)`;
          well.style.boxShadow = WELL_SHADOW;
        }}
      />
    </div>
  );
}

// Sort key for the Online tab: ready to play first, busy last.
function activityRank(p) {
  const a = p.activity || '';
  if (p.lfg || /^Looking/i.test(a)) return 0;
  if (p.presenceStatus === 'away' || p.presenceStatus === 'dnd') return 3;
  if (/match|draft/i.test(a)) return 2;
  return 1;
}

function isInMatch(p) {
  return /match|draft/i.test(p.activity || '') && !/^Looking/i.test(p.activity || '');
}

function HallActionButton({ hint, onClick, children, primary }) {
  return (
    <Tooltip content={hint} side="top" delay={300}>
      <button
        type="button"
        aria-label={hint}
        className="inline-flex items-center justify-center cursor-pointer"
        style={primary
          ? { ...GOLD_BTN, width: '1.75cqw', height: '1.75cqw' }
          : { width: '1.75cqw', height: '1.75cqw', borderRadius: 'var(--radius-md)', background: 'rgba(20,14,6,0.85)', border: `1px solid ${GOLD} 0.3)`, color: ACCENT_GOLD }}
        onClick={(e) => { e.stopPropagation(); playUI(UI.CLICK); onClick(); }}
      >
        {children}
      </button>
    </Tooltip>
  );
}

// Player-facing summary of an LFG mode set. ranked/casual are stakes of
// Constructed, so they fold into one entry.
function lfgModeLabels(modes) {
  const set = new Set(modes || []);
  const out = [];
  if (set.has('draft')) out.push(t('Draft'));
  if (set.has('sealed')) out.push(t('Sealed'));
  if (set.has('ranked') && set.has('casual')) out.push(t('Constructed'));
  else if (set.has('ranked')) out.push(t('Constructed (Ranked)'));
  else if (set.has('casual')) out.push(t('Constructed (Casual)'));
  if (set.has('sandbox')) out.push(t('Sandbox'));
  return out;
}

function lfgLine(p) {
  const labels = p.lfg ? lfgModeLabels(p.lfg.modes) : [];
  return labels.length ? t('Looking for {modes}', { modes: labels.join(' · ') }) : null;
}

// Hover pill on a ladder row. Opens the shared challenge menu.
class LadderChallenge extends Component {
  state = { menu: false };

  render() {
    const { player, onChallenge } = this.props;
    return (
      <div
        className={`absolute inline-flex items-center transition-opacity ${this.state.menu ? '' : 'opacity-0 group-hover:opacity-100'}`}
        style={{ right: '0.75cqw', top: '50%', transform: 'translateY(-50%)' }}
        onMouseLeave={() => this.state.menu && this.setState({ menu: false })}
      >
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2.5 py-1 text-[0.6cqw] font-semibold cursor-pointer active:scale-[0.96]"
          style={{ ...GOLD_BTN, transition: 'transform 160ms cubic-bezier(0.23, 1, 0.32, 1)' }}
          onClick={(e) => { e.stopPropagation(); playUI(UI.CLICK); this.setState((s) => ({ menu: !s.menu })); }}
        >
          <Swords style={{ width: '0.8cqw', height: '0.8cqw' }} />
          {t('Challenge')}
        </button>
        {this.state.menu ? (
          <ChallengeMenu
            player={player}
            style={{ position: 'absolute', right: 0, top: '100%', marginTop: '0.25cqw', zIndex: 20 }}
            onPick={(mode) => { this.setState({ menu: false }); onChallenge(player, mode); }}
          />
        ) : null}
      </div>
    );
  }
}

// One online player. Click opens the profile; hover reveals the actions.
// Challenge asks which mode first: Match runs through the full setup
// (format, stakes, rules), Sandbox only asks for rules.
class HallPlayerRow extends Component {
  state = { menu: false };

  render() {
    const { player, isMe, isFriend, requestSent, sorceryCards, onView, onChallenge, onChat, onAddFriend } = this.props;
    const tierHex = TIER_HEX[player.tier] || '#A6A09B';
    const looking = lfgLine(player);
    const canAct = !isMe;
    return (
      <div className="relative group" style={{ height: '3.25cqw' }} onMouseLeave={() => this.state.menu && this.setState({ menu: false })}>
        <button
          type="button"
          className="relative flex items-center gap-3 px-3 transition-[background,border-color] cursor-pointer w-full text-left h-full"
          style={{
            borderRadius: HALL_RADIUS,
            isolation: 'isolate',
            background: isMe ? 'linear-gradient(90deg, rgba(212,168,67,0.22) 0%, rgba(212,168,67,0.08) 45%, transparent 90%)' : 'transparent',
            border: isMe ? `1px solid ${GOLD} 0.35)` : '1px solid transparent',
          }}
          onMouseEnter={(e) => { if (!isMe) e.currentTarget.style.background = 'linear-gradient(90deg, rgba(166,160,155,0.08) 0%, transparent 80%)'; }}
          onMouseLeave={(e) => { if (!isMe) e.currentTarget.style.background = 'transparent'; }}
          onClick={() => player.id && onView(player.id)}
        >
          <RowArtBackdrop player={player} sorceryCards={sorceryCards} borderRadius={HALL_RADIUS} />
          <div className="relative shrink-0">
            <RowAvatar player={player} sorceryCards={sorceryCards} />
            <span
              className="absolute rounded-full"
              style={{ right: '-0.1cqw', bottom: '-0.1cqw', width: '0.6cqw', height: '0.6cqw', background: player.presenceStatus === 'away' ? '#d9a441' : player.presenceStatus === 'dnd' ? '#c45050' : ONLINE_DOT, border: '1px solid #1a140a', boxShadow: `0 0 5px ${ONLINE_DOT}` }}
            />
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <div className="min-w-0 flex flex-col">
              <span className="font-semibold truncate text-[0.8125cqw] min-w-0" style={{ color: isMe ? ROW_TEXT_SELF : ROW_TEXT, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                {player.name}
              </span>
              <span className="truncate text-[0.6cqw]" style={{ color: looking ? ACCENT_GOLD : TEXT_MUTED, fontWeight: looking ? 600 : 400 }}>
                {player.tier ? (
                  <span className="font-bold" style={{ color: tierHex }}>{formatRank(player.tier, player.division)}<span style={{ color: TEXT_MUTED, fontWeight: 400 }}> · </span></span>
                ) : null}
                {looking || player.activity || t('Online')}
                {looking && player.lfg?.widened ? <span style={{ color: TEXT_MUTED, fontWeight: 400 }}> · {t('any rank')}</span> : null}
              </span>
            </div>
            <PlayerBadgeStrip player={player} sorceryCards={sorceryCards} size="1.5cqw" />
            {player.discordHandle ? <DiscordHandlePill handle={player.discordHandle} size="sm" withAt className="shrink-0" /> : null}
          </div>
          {isFriend ? (
            <span className="shrink-0 text-[0.55cqw] font-semibold" style={{ color: `${GOLD} 0.5)` }}>{t('Friend')}</span>
          ) : null}
        </button>
        {canAct ? (
          <div className={`absolute inline-flex items-center gap-1 transition-opacity ${this.state.menu ? '' : 'opacity-0 group-hover:opacity-100'}`} style={{ right: '0.75cqw', top: '50%', transform: 'translateY(-50%)' }}>
            {isFriend ? (
              <HallActionButton hint={t('Chat')} onClick={() => onChat(player)}>
                <MessageSquare style={{ width: '0.85cqw', height: '0.85cqw' }} />
              </HallActionButton>
            ) : (
              <HallActionButton hint={requestSent ? t('Friend request sent') : t('Add friend')} onClick={() => { if (!requestSent) onAddFriend(player); }}>
                <UserPlus style={{ width: '0.85cqw', height: '0.85cqw', opacity: requestSent ? 0.4 : 1 }} />
              </HallActionButton>
            )}
            <HallActionButton hint={t('Challenge to a match')} primary onClick={() => this.setState((s) => ({ menu: !s.menu }))}>
              <Swords style={{ width: '0.85cqw', height: '0.85cqw' }} />
            </HallActionButton>
          </div>
        ) : null}
        {this.state.menu ? (
          <ChallengeMenu
            player={player}
            style={{ position: 'absolute', right: '0.75cqw', top: '100%', zIndex: 20 }}
            onPick={(mode) => { this.setState({ menu: false }); onChallenge(player, mode); }}
          />
        ) : null}
      </div>
    );
  }
}

export default class ArenaHub extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showAdmin: false,
      inspectedCard: null,
      showCredits: false,
      // Hub changelog popup. Notes are fetched lazily on first open and
      // cached for the session so reopening doesn't re-hit GitHub.
      showChangelog: false,
      changelogLoading: false,
      changelogData: null,
      // Sparse leaderboard model: rows is indexed by absolute offset in
      // the current filtered view, total is the server's count for that
      // view. Pages are fetched on demand as the user scrolls.
      leaderboardRows: [],
      leaderboardTotal: 0,
      leaderboardInitialLoading: true,
      leaderboardFilter: 'all',
      leaderboardSearch: '',
      // Which leaderboard view is showing. 'ranked' is the constructed
      // 1v1 ladder (tier/division/LP). 'limited' bundles draft + sealed
      // tournament play, 'sandbox' is the practice mode; both rank by
      // per-mode wins/losses. Casual matches are private and have no
      // ladder at all. None
      // of the non-ranked results bleed into the ranked view. Different
      // columns on the server. The last chosen tab is persisted across
      // sessions in localStorage.
      leaderboardMode: readLeaderboardMode(),
      // The caller's absolute rank in the unfiltered ladder, used by
      // the "Find me" button. Null until the /me fetch resolves.
      selfRank: null,
      canvasScale: getCanvasScale(),
      showHubTutorial: false,
      // PlayerTitle resolves asynchronously against the Hall of Fame
      // cache. Tracking the resolution here lets us append a trailing
      // comma to the H1 username only when a title is actually about
      // to render below it.
      selfHasTitle: false,
      // Leaderboard player IDs whose crown title resolved. Drives the
      // trailing comma on the player's name in each row.
      leaderboardIdsWithTitle: new Set(),
      // Hall panel: Players (online right now) / Leaderboard. Players is
      // the default because it is the only tab with something to do on it.
      hallTab: 'players',
      onlinePlayers: [],
      onlineLoading: true,
      playersSearch: '',
      playersFilter: 'all',
      friendRequestsSent: new Set(),
      lfg: lfg.getState(),
      devDemoPlayers: false,
      // Whether the hub shows the player's profile background instead
      // of the stock keyart. Toggled from the Edit Profile modal; the
      // subscription in componentDidMount keeps this live while the
      // profile overlay is open on top of the mounted hub.
      useProfileBg: getHubProfileBg(),
    };
    // Tracks in-flight page offsets so we don't refetch the same chunk
    // when the visible-range callback fires repeatedly during scroll.
    this._loadingPages = new Set();
    // Bumped on every filter/search reset so a stale in-flight page
    // fetch can be discarded when it resolves after a newer reset.
    this._fetchEpoch = 0;
    this._leaderboardRef = createRef();
    // 1-in-15 hub opens, the Support Development button plays a short
    // gold aura pulse as a contribution reminder. Rolled once per
    // mount; the hub remounts on every return from a sub-screen, so
    // this is genuinely per-open.
    this._supportAura = Math.random() < 1 / 15;
  }

  componentDidMount() {
    this.unsubCanvasScale = onCanvasScaleChange((scale) => this.setState({ canvasScale: scale }));
    this._unsubHubBg = onHubProfileBgChange((on) => this.setState({ useProfileBg: on }));
    this.refreshLeaderboard();
    this.refreshSelfRank();
    this._unsubOnline = onlinePlayers.subscribe(this.applyOnlinePlayers);
    this._unsubLfg = lfg.subscribe((st) => {
      this.setState({ lfg: st });
      this.refreshOnlinePlayers();
    });
    this.maybeStartHubTutorial();
    // Prime the protected-mat registry, THEN warm the playmat assets.
    // The ordering matters: prewarming before the registry PUT lands
    // makes every protected preview/full fetch 404, so the store opens
    // cold and the bytes never reach disk. Sequencing here guarantees
    // the registry is live before any warm fetch fires.
    this.initPlaymatAssets();
  }

  componentDidUpdate(prevProps) {
    // If the parent forces a tutorial replay (from the Settings
    // screen), surface the overlay again. The parent clears its
    // request flag right after mount via the onDismiss callback.
    if (!prevProps.replayHubTutorial && this.props.replayHubTutorial) {
      this.setState({ showHubTutorial: true });
    }
    // Profile.id can swap between accounts during a single session
    // (rapid sign-out/sign-in for testing). Re-prime the registry +
    // re-warm so the protected-mat watermark embeds the new userId.
    if (prevProps.profile?.id !== this.props.profile?.id) {
      this.initPlaymatAssets();
    }
  }

  // Push the player's userId + the protected-playmat blob URLs from
  // the cosmetics catalog into the main process so it can fetch +
  // watermark + cache the licensed mats on demand. Fire-and-forget;
  // the local server holds the registry in memory only and tolerates
  // a missed push (PlaymatPicker re-fetches cosmetics on open, which
  // re-triggers the push).
  async primeProtectedPlaymatRegistry() {
    const id = this.props.profile?.id;
    if (!id) return;
    setCosmeticsUserId(id);
    try { await getCosmeticsCached(); } catch (_) {}
    // Wait for the registry PUT to actually reach the local server, not
    // just for the catalog fetch. The PUT is what unlocks the
    // protected-playmat endpoint.
    try { await awaitProtectedRegistry(); } catch (_) {}
  }

  // Prime the registry, then background-warm the playmat assets: small
  // preview thumbnails for every mat (store grid + picker dropdown) and
  // full art for owned protected mats (instant match loads). Dynamically
  // imports PlaymatPicker so its bundle isn't pulled in eagerly here.
  async initPlaymatAssets() {
    await this.primeProtectedPlaymatRegistry();
    if (this._unmounted) return;
    try {
      const m = await import('./PlaymatPicker');
      await m.prewarmPlaymatPreviews?.();
    } catch (_) {}
  }

  /**
   * Decide whether to auto-play the hub tutorial on mount. Awaits
   * the tutorial state hydration so we don't show the overlay on a
   * stale miss (the on-disk store might have the seen flag even
   * when localStorage is empty on a fresh CEF launch).
   */
  async maybeStartHubTutorial() {
    const { profile } = this.props;
    const id = profile?.id;
    if (!id) return;
    await hydrateTutorialState();
    if (this._unmounted) return;
    if (!shouldAutoPlay(id, HUB_TUTORIAL_KEY)) return;
    this.setState({ showHubTutorial: true });
  }

  handleHubTutorialDismiss = () => {
    const { profile, onHubTutorialDismissed } = this.props;
    if (profile?.id) markTutorialSeen(profile.id, HUB_TUTORIAL_KEY);
    this.setState({ showHubTutorial: false });
    onHubTutorialDismissed?.();
  };

  componentWillUnmount() {
    this._unmounted = true;
    this._unsubOnline?.();
    this._unsubLfg?.();
    this.unsubCanvasScale?.();
    this._unsubHubBg?.();
    clearTimeout(this._searchDebounce);
  }

  // Page size matches the server's default LIMIT. Smaller than the
  // viewport's overscan window so two adjacent pages share a small
  // overlap of visible rows and the user never sees a gap of
  // placeholders during fast scroll.
  static LEADERBOARD_PAGE_SIZE = 50;

  // Reset rows + total and fetch page 0 for the current filter/search.
  // Called on mount, on filter change, after a debounced search input,
  // and after "Find me" clears the filter.
  refreshLeaderboard = () => {
    const epoch = ++this._fetchEpoch;
    this._loadingPages = new Set();
    this.setState(
      { leaderboardRows: [], leaderboardTotal: 0, leaderboardInitialLoading: true },
      () => this.loadLeaderboardPage(0, epoch),
    );
  };

  refreshSelfRank = async () => {
    const mode = this.state.leaderboardMode;
    try {
      const { getSelfLeaderboardRank } = await import('../utils/arena/matchmakingApi');
      const me = await getSelfLeaderboardRank({ mode });
      if (this._unmounted) return;
      // Discard if the user flipped the mode while the fetch was in
      // flight. Otherwise a slow response from the previous mode
      // would clobber the just-requested one.
      if (this.state.leaderboardMode !== mode) return;
      this.setState({ selfRank: me });
    } catch {
      // /me failure is non-fatal. Find Me just stays disabled.
    }
  };

  loadLeaderboardPage = async (offset, epoch = this._fetchEpoch) => {
    if (this._loadingPages.has(offset)) return;
    this._loadingPages.add(offset);
    const { leaderboardFilter, leaderboardSearch, leaderboardMode } = this.state;
    // Tier filter only narrows the ranked view; the other modes don't
    // carry a tier dimension at all.
    const tier = leaderboardMode === 'ranked' && leaderboardFilter !== 'all' ? leaderboardFilter : null;
    const search = leaderboardSearch.trim();
    try {
      const { getLeaderboard } = await import('../utils/arena/matchmakingApi');
      const page = await getLeaderboard({
        offset,
        limit: ArenaHub.LEADERBOARD_PAGE_SIZE,
        tier,
        search,
        mode: leaderboardMode,
      });
      if (this._unmounted) return;
      // Discard if the user changed filter/search while we were inflight.
      if (epoch !== this._fetchEpoch) return;
      const rows = Array.isArray(page?.rows) ? page.rows : [];
      const total = typeof page?.total === 'number' ? page.total : 0;
      this.setState((s) => {
        // Dev-only mock fallback: only when the very first unfiltered
        // fetch comes back genuinely empty. Never replaces real data
        // and never fires on filtered/searched views. Limited / sandbox
        // have no mock data. Those views can legitimately be empty
        // while feature rolls out.
        if (
          offset === 0 &&
          total === 0 &&
          rows.length === 0 &&
          isDevBuild() &&
          !tier &&
          !search &&
          leaderboardMode === 'ranked'
        ) {
          return {
            leaderboardRows: MOCK_LEADERBOARD,
            leaderboardTotal: MOCK_LEADERBOARD.length,
            leaderboardInitialLoading: false,
          };
        }
        const next = s.leaderboardRows.slice();
        if (next.length < total) next.length = total;
        for (let i = 0; i < rows.length; i++) {
          next[offset + i] = rows[i];
        }
        return {
          leaderboardRows: next,
          leaderboardTotal: total,
          leaderboardInitialLoading: false,
        };
      });
    } catch {
      // Leave the placeholders in place; a future scroll will retry.
    } finally {
      this._loadingPages.delete(offset);
    }
  };

  // VirtualList -> parent hook. Identify which page-aligned offsets
  // overlap the visible window and trigger a fetch for any that haven't
  // already been requested. Idempotent. LoadLeaderboardPage early-returns
  // for offsets already in-flight or already populated.
  handleLeaderboardVisibleRange = (startIdx, endIdx) => {
    const PAGE = ArenaHub.LEADERBOARD_PAGE_SIZE;
    const firstPage = Math.floor(startIdx / PAGE);
    const lastPage = Math.floor(Math.max(startIdx, endIdx - 1) / PAGE);
    const { leaderboardRows } = this.state;
    for (let p = firstPage; p <= lastPage; p++) {
      const offset = p * PAGE;
      if (leaderboardRows[offset] === undefined) {
        this.loadLeaderboardPage(offset);
      }
    }
  };

  setLeaderboardFilter = (value) => {
    if (value === this.state.leaderboardFilter) return;
    this.setState({ leaderboardFilter: value }, this.refreshLeaderboard);
  };

  // Dev: a populated Online tab without a populated server. Ladder rows
  // dressed up with activities, presence and a few searches so the list
  // can be looked at with more than one or two real people on it.
  demoOnlinePlayers = async () => {
    const { getLeaderboard } = await import('../utils/arena/matchmakingApi');
    const page = await getLeaderboard({ offset: 0, limit: 14 });
    const ACTIVITY = [
      { activity: 'In the hub', presenceStatus: 'online', lfg: { modes: ['draft', 'sealed', 'ranked'], widened: false } },
      { activity: 'In the hub', presenceStatus: 'online', lfg: { modes: ['sandbox'], widened: true } },
      { activity: 'Ranked match vs Grimjaw', presenceStatus: 'online', lfg: null },
      { activity: 'In the deck builder', presenceStatus: 'online', lfg: null },
      { activity: 'In the hub', presenceStatus: 'online', lfg: { modes: ['casual', 'sandbox'], widened: false } },
      { activity: 'Drafting Gothic', presenceStatus: 'online', lfg: null },
      { activity: 'Browsing the store', presenceStatus: 'online', lfg: null },
      { activity: 'In the hub', presenceStatus: 'away', lfg: null },
      { activity: 'In a match vs Fenwick Moss', presenceStatus: 'online', lfg: null },
      { activity: 'Building a draft deck', presenceStatus: 'online', lfg: null },
      { activity: 'At the auction house', presenceStatus: 'dnd', lfg: null },
      { activity: 'In the hub', presenceStatus: 'online', lfg: { modes: ['ranked'], widened: false } },
      { activity: 'Opening packs', presenceStatus: 'online', lfg: null },
      { activity: 'Choosing a deck', presenceStatus: 'online', lfg: null },
    ];
    return (page?.rows || []).map((p, i) => ({ ...p, online: true, id: `demo-${p.id}`, ...ACTIVITY[i % ACTIVITY.length] }));
  };

  refreshOnlinePlayers = () => onlinePlayers.refresh();

  // The shared online poll (onlinePlayersStore) feeds the Hall's Online
  // tab; the Tavern header counts the same rows.
  applyOnlinePlayers = async ({ rows }) => {
    const demo = this.state.devDemoPlayers ? await this.demoOnlinePlayers().catch(() => []) : [];
    if (this._unmounted) return;
    this.setState({ onlinePlayers: [...rows, ...demo], onlineLoading: false });
  };

  setHallTab = (tab) => {
    this.setState({ hallTab: tab });
    if (tab === 'players') this.refreshOnlinePlayers();
  };

  handleAddFriend = async (player) => {
    try {
      const { sendFriendRequest } = await import('../utils/friendsApi');
      await sendFriendRequest(player.id);
      this.setState((s) => ({ friendRequestsSent: new Set([...s.friendRequestsSent, player.id]) }));
      toast.success(t('Friend request sent to {name}', { name: player.name }));
    } catch (err) {
      toast.error(err.message || t('Friend request failed'));
    }
  };


  // Flip the leaderboard between Ranked / Limited / Sandbox views.
  // Mode is server-side authoritative (it picks which columns drive
  // ORDER BY), so we refresh the page list AND the self-rank pill in
  // lockstep. The tier filter only makes sense on Ranked, so any
  // non-'all' filter is cleared when leaving Ranked.
  setLeaderboardMode = (value) => {
    if (value === this.state.leaderboardMode) return;
    const nextFilter = value === 'ranked' ? this.state.leaderboardFilter : 'all';
    writeLeaderboardMode(value);
    // Keep the current selfRank pill visible through the swap. Clearing
    // it to null here made "Find Me" disappear and flash back in once the
    // refetch resolved. refreshSelfRank discards stale responses and
    // updates the value (or hides the pill if the new mode has no rank).
    this.setState(
      { leaderboardMode: value, leaderboardFilter: nextFilter },
      () => {
        this.refreshLeaderboard();
        this.refreshSelfRank();
      },
    );
  };

  setLeaderboardSearch = (value) => {
    this.setState({ leaderboardSearch: value });
    clearTimeout(this._searchDebounce);
    // 250ms is below typing latency. Short enough to feel snappy,
    // long enough to coalesce a typical word into one query.
    this._searchDebounce = setTimeout(this.refreshLeaderboard, 250);
  };

  jumpToSelfRank = async () => {
    const { selfRank, leaderboardFilter, leaderboardSearch } = this.state;
    if (!selfRank?.rank) return;
    // The /me rank is computed against the unfiltered ladder, so any
    // active filter has to be cleared first. We refresh and wait for
    // page 0 to settle, then scroll to the absolute index.
    if (leaderboardFilter !== 'all' || leaderboardSearch.trim()) {
      await new Promise((resolve) =>
        this.setState({ leaderboardFilter: 'all', leaderboardSearch: '' }, () => {
          this.refreshLeaderboard();
          resolve();
        }),
      );
    }
    const idx = selfRank.rank - 1;
    const PAGE = ArenaHub.LEADERBOARD_PAGE_SIZE;
    const pageOffset = Math.floor(idx / PAGE) * PAGE;
    // Make sure the surrounding page is loaded before scrolling so the
    // row isn't blank when the user lands on it.
    await this.loadLeaderboardPage(pageOffset);
    if (this._unmounted) return;
    this._leaderboardRef.current?.scrollToIndex(idx, { center: true });
  };

  getAvatarImageUrl(cardId) {
    const { sorceryCards } = this.props;
    if (!sorceryCards || !cardId) return null;
    const card = sorceryCards.find((c) => c.unique_id === cardId);
    return card?.printings?.[0]?.image_url || null;
  }

  openChangelog = async () => {
    this.setState({ showChangelog: true });
    if (this.state.changelogData) return;
    this.setState({ changelogLoading: true });
    const data = await fetchReleaseNotes(this.props.updateStatus?.currentVersion);
    this.setState({ changelogData: data, changelogLoading: false });
  };

  closeChangelog = () => this.setState({ showChangelog: false });

  render() {
    const { profile, rank, onOpenPlay, onOpenStore, onOpenDeckBuilder, onOpenAuctionHouse, onOpenArcaneTrials, onOpenSettings, onOpenSkillTree, onQuit, updateStatus, onViewProfile, onExit, friendListData, onToggleFriends, onToggleMailbox, mailboxUnreadCount } = this.props;
    const {
      leaderboardInitialLoading,
      leaderboardFilter,
      leaderboardSearch,
      leaderboardRows,
      leaderboardTotal,
      leaderboardMode,
      hallTab,
      selfRank,
    } = this.state;
    const progress = xpProgressInLevel(profile.xp);
    const level = progress.level;
    const collectionSize = profile.collection.reduce((sum, c) => sum + c.quantity, 0);
    const uniqueCards = profile.collection.length;
    const avatarUrl = this.getAvatarImageUrl(profile.profileAvatar);
    const rankColor = TIER_COLORS[rank?.tier] || 'text-white';
    // matchHistory is a capped recency list (server caps it at 50), so
    // .length saturates as soon as the user crosses the cap. Wins/losses
    // are monotonic counters maintained atomically by the server on
    // every match claim. Same source FriendProfileOverlay uses.
    const winCount = profile.wins || 0;
    const lossCount = profile.losses || 0;
    const totalMatches = winCount + lossCount;
    const winRate = totalMatches > 0 ? Math.round((winCount / totalMatches) * 100) : 0;

    const { sorceryCards } = this.props;
    const ownedCardIds = new Set((profile.collection || []).map((c) => c.cardId));
    const setNameMap = { Gothic: 'gothic', 'Arthurian Legends': 'arthurian', Beta: 'beta' };
    const setStats = { gothic: { owned: 0, total: 0 }, arthurian: { owned: 0, total: 0 }, beta: { owned: 0, total: 0 } };
    if (sorceryCards) {
      for (const card of sorceryCards) {
        const cardSets = new Set((card.printings || []).map((p) => p.set_id));
        for (const setName of cardSets) {
          const key = setNameMap[setName];
          if (key) {
            setStats[key].total++;
            if (ownedCardIds.has(card.unique_id)) setStats[key].owned++;
          }
        }
      }
    }

    // The player's profile background, shown in place of the stock
    // keyart when the header toggle is on. HeroBackdrop is the same
    // treatment the profile overlay uses: the art sharp as a banner
    // across the top, torn edge, and a blurred wash of the same art
    // running to the bottom of the screen.
    const profileBg = this.state.useProfileBg ? resolveProfileBackdrop(profile, sorceryCards) : null;

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden select-none" style={{ background: '#08080a' }}>

        {profileBg ? (
          <HeroBackdrop art={profileBg} />
        ) : (
          <div className="absolute inset-0" style={{ background: `url('/hub-bg.webp') center/cover no-repeat` }} />
        )}

        {/* Vignette. Tuned for the stock keyart and skipped for the
            profile backdrop, which carries its own darkening inside
            HeroBackdrop; stacking both read as double-darkened. */}
        {!profileBg ? (
          <>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 85% 75% at 50% 50%, transparent 35%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.88) 100%)' }} />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 25%, transparent 70%, rgba(0,0,0,0.55) 100%)' }} />
          </>
        ) : null}

        {/* Ambient particle effects (embers, dust, sparks) */}
        <AmbientParticles />

        {/* Build identifier. Keeps the running version and beta status
            visible at a glance from the hub's bottom-left corner. Clickable:
            opens the changelog for the version the player is on. */}
        {updateStatus?.currentVersion ? (
          <button
            type="button"
            title={t('View changelog')}
            className="absolute bottom-3 left-4 z-20 flex flex-col items-start leading-tight select-none transition-opacity hover:opacity-80 p-2 -m-2"
            onClick={() => { playUI(UI.CLICK); this.openChangelog(); }}
          >
            <span
              className="text-[0.5625cqw] font-bold arena-heading"
              style={{ color: `${GOLD} 0.6)`, textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}
            >
              {t('Open Beta')}
            </span>
            <span
              className="text-[0.625cqw] font-semibold tabular-nums"
              style={{ color: TEXT_MUTED, textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}
            >
              v{updateStatus.currentVersion}
            </span>
          </button>
        ) : null}

        {/* ─── TOP BAR ─────────────────────────────────────── */}
        <AppHeader
          profile={profile}
          onToggleMailbox={onToggleMailbox}
          mailboxUnreadCount={mailboxUnreadCount}
          mailboxOpen={this.props.mailboxOpen}
          mailboxDropdown={this.props.mailboxDropdown}
          draftQueueDropdown={this.props.draftQueueDropdown}
          onToggleFriends={onToggleFriends}
          friendListData={friendListData}
          onOpenSkillTree={onOpenSkillTree}
        >
          {/* Staff cluster. The Admin button and, on the dev server,
              the role-preview switcher that reskins the panel behind
              it. Grouped in their own flex row so the pair sits tight
              instead of inheriting the header's 24 px rhythm. */}
          <div className="flex items-center gap-2">
            {this.props.isAdmin && (
              <button
                type="button"
                className="px-3 py-1 text-[0.625cqw] font-bold transition-transform hover:scale-[1.05] active:scale-[0.96]"
                style={{
                  background: 'linear-gradient(180deg, rgba(180,60,60,0.15) 0%, rgba(120,30,30,0.1) 100%)',
                  border: '1px solid rgba(180,60,60,0.35)',
                  borderRadius: 'var(--radius-sm)',
                  color: '#c45050',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                }}
                onClick={() => this.setState({ showAdmin: true })}
              >
                {t('Admin')}
              </button>
            )}
            {/* Renders for nobody in a shipped build and for nobody but
                an owner on the local server. The gate lives in the
                component. */}
            <RolePreviewButton profile={profile} />
          </div>
        </AppHeader>

        {/* ─── MAIN CONTENT ────────────────────────────────── */}
        <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
          {/* Outer layout is a ROW so the left menu can span the full
              content height and stay vertically centred in the window
              regardless of how tall the hero / leaderboard become. The
              right-side wrapper below holds everything else (hero +
              leaderboard + achievements) and owns its own flex-col. */}
          <div className="mx-auto px-8 w-full flex gap-5 overflow-hidden flex-1">

            {/* ── LEFT COLUMN: VERTICAL MENU (full height) ── */}
            {/* justify-center anchors the logo + menu stack to the
                window's vertical midpoint. This column sits as a
                direct sibling of the right-side wrapper, so its
                available height is the full main-content area, not
                just the space below the hero. */}
            <div className="hub-panel-enter w-[17.5cqw] shrink-0 flex flex-col justify-center overflow-visible py-1 pl-1">

              <div className="relative w-full mb-3">
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: '50%',
                    left: '50%',
                    width: '130%',
                    height: '200%',
                    transform: 'translate(-50%, -50%)',
                    background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.5) 25%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 70%)',
                  }}
                />
                <img
                  src="/valkenhall-logo.png"
                  alt="Valkenhall"
                  width={1613}
                  height={777}
                  className="relative w-full h-auto"
                  draggable={false}
                />
                <BetaBadge />
              </div>

              <MenuButton title={t('Play')} onClick={this.props.onOpenPlay} dataTutorial="play" />
              {isDevServerBuild() && this.props.onDevQuickMatch ? (
                <MenuButton title="Dev Quick Match" onClick={this.props.onDevQuickMatch} />
              ) : null}
              {isDevServerBuild() && this.props.onDevMatchFound ? (
                <MenuButton title="Dev Match Found" onClick={() => this.props.onDevMatchFound('instance', 'host', 'ranked')} />
              ) : null}
              {isDevServerBuild() ? (
                <MenuButton
                  title={this.state.devDemoPlayers ? 'Dev Demo Players: on' : 'Dev Demo Players'}
                  onClick={() => this.setState((s) => ({ devDemoPlayers: !s.devDemoPlayers, hallTab: 'players' }), this.refreshOnlinePlayers)}
                />
              ) : null}
              <MenuButton title={t('Deck Builder')} onClick={onOpenDeckBuilder} dataTutorial="deck-builder" />
              <MenuButton title={t('Store')} onClick={onOpenStore} dataTutorial="store" />
              <MenuButton title={t('Arcane Trials')} onClick={onOpenArcaneTrials} dataTutorial="arcane-trials" />
              <MenuButton title={t('Auction House')} onClick={onOpenAuctionHouse} dataTutorial="auction-house" />
              <div className="relative" data-tutorial="settings">
                <MenuButton title={t('Settings')} onClick={onOpenSettings} />
                {updateStatus && (updateStatus.state === 'READY_TO_INSTALL' || updateStatus.state === 'DOWNLOADING' || updateStatus.state === 'DOWNLOAD_FAILED') ? (
                  <div
                    className="absolute top-2.5 right-2.5 w-2.5 h-2.5 rounded-full"
                    style={{
                      background: updateStatus.state === 'DOWNLOAD_FAILED' ? '#b04040' : ACCENT_GOLD,
                      boxShadow: `0 0 6px ${updateStatus.state === 'DOWNLOAD_FAILED' ? 'rgba(176,64,64,0.6)' : 'rgba(212,168,67,0.6)'}`,
                    }}
                  />
                ) : null}
              </div>

              {/* Quit Game. Same forged-plate treatment as the menu
                  stack, re-tinted in blood-iron. Lives here rather
                  than only in the Esc menu so players can shut down
                  from the hub without opening the pause overlay. */}
              {onQuit ? (
                <MenuButton title={t('Quit Game')} onClick={onQuit} variant="danger" />
              ) : null}

              {/* Support Development. Secondary tip-jar action. Shares
                  MenuButton's recipe so it reads as part of the same
                  forged-plate stack. Renders as an `<a>` (href triggers
                  link mode in MenuButton) and routes via target="_blank"
                  → main process's setWindowOpenHandler → shell.openExternal. */}
              <MenuButton
                title={t('Support Development')}
                icon={<Coffee style={{ width: '0.875cqw', height: '0.875cqw' }} />}
                href={SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                compact
                aura={this._supportAura}
              />

              {/* Credits. Low-visibility text link centered beneath the
                  menu stack. Plain word rather than a forged plate so it
                  stays out of the way of the primary actions while
                  remaining discoverable. */}
              <button
                type="button"
                onClick={() => this.setState({ showCredits: true })}
                className="arena-heading self-center text-[1cqw] tracking-wide transition-colors cursor-pointer px-3 py-1.5"
                style={{
                  color: 'rgba(186,180,175,0.85)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = ACCENT_GOLD)}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(186,180,175,0.85)')}
              >
                {t('Credits')}
              </button>

            </div>

            {/* ── RIGHT SIDE: 2-column CSS grid ──
                Left column holds a vertical stack (hero → divider →
                leaderboard). Right column holds the achievements panel
                spanning full height. Using grid means leaderboard +
                achievements share the same parent, while the hero is
                naturally constrained to the leaderboard column's
                width. So w-[60%] mx-auto on the hero centers it over
                the leaderboard, never over the achievements. */}
            {/* overflow-hidden is intentionally NOT set here. The
                CornerPlating elements on each inner panel use a -2px
                offset to overhang the panel edge, and clipping the
                grid wrapper would amputate those plating corners. The
                inner leaderboard / achievements panels already handle
                their own vertical scrolling internally. */}
            <div className="flex-1 min-w-0 grid gap-5 py-4 min-h-0"
                 style={{ gridTemplateColumns: '1fr 26cqw' }}>

            {/* ── LEFT GRID CELL: hero + divider + leaderboard ── */}
            {/* No overflow-hidden for the same reason as the parent. */}
            <div className="min-w-0 flex flex-col min-h-0">

            {/* ─── HERO: PLAYER IDENTITY ──────────────────── */}
            <div className="hub-glass-panel hub-panel-enter hub-enter-2 relative flex items-center gap-8 py-5 px-6 shrink-0 w-full min-w-0" style={GLASS_PANEL_STYLE}>
              <GlassSurface />
              <CornerPlating position="top-left" color={`${GOLD} 0.65)`} />
              <CornerPlating position="top-right" color={`${GOLD} 0.65)`} />
              <CornerPlating position="bottom-left" color={`${GOLD} 0.65)`} />
              <CornerPlating position="bottom-right" color={`${GOLD} 0.65)`} />
              {/* Avatar. Aged-bronze Viking medallion frames a portrait
                  seated into a recessed well. The medallion reads as
                  engraved metalwork (dark base + muted gold mask + soft
                  inner shadow) rather than a bright gold disc, so the
                  portrait stays the focal point. */}
              <button
                type="button"
                data-tutorial="profile-avatar"
                className="relative group shrink-0 cursor-pointer transition-transform duration-150 active:scale-[0.98]"
                style={{ width: '8.75cqw', height: '8.75cqw', isolation: 'isolate' }}
                onClick={() => onViewProfile?.(profile.id)}
                title={t('View your profile')}
              >
                {/* Ember halo. Sits behind the base plate so it only
                    reads as warm light leaking out past the medallion
                    rim. Pulses + scales continuously for an "alive"
                    feel; see .avatar-medallion-ember in app.css. */}
                <div className="avatar-medallion-ember" aria-hidden="true" />
                {/* Unclaimed achievement rewards. The rich panel moved to
                    the profile view, so this is the hub's only hint that
                    keystones are waiting behind the avatar. */}
                {(profile.unclaimedAchievements?.length || 0) > 0 ? (
                  <span
                    className="absolute -top-1 -right-1 z-10 inline-flex items-center px-2 py-0.5 rounded-full text-[0.5625cqw] font-bold tabular-nums arena-achievement-unclaimed-pulse"
                    style={{ background: '#3a2c12', border: `1px solid ${ACCENT_GOLD}`, color: ACCENT_GOLD, textShadow: `0 0 8px ${GOLD} 0.5)` }}
                  >
                    {t('{n} to claim', { n: profile.unclaimedAchievements.length })}
                  </span>
                ) : null}

                {/* Base plate. Single dark circle with a thin gold rim.
                    Drop shadow lifts the whole badge off the hero panel;
                    the inset shadow carves the frame so the medallion
                    below feels like it sits in a bowl. No outer glow
                    ring. One border + one subtle halo is enough. */}
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'radial-gradient(circle at center, rgba(14,10,6,0.96) 0%, rgba(6,4,2,0.98) 100%)',
                    border: `1px solid ${GOLD} 0.42)`,
                    boxShadow: `
                      inset 0 3px 10px rgba(0,0,0,0.85),
                      inset 0 -1px 0 ${GOLD} 0.1),
                      0 6px 22px rgba(0,0,0,0.65),
                      0 0 20px rgba(180,140,60,0.08)
                    `,
                  }}
                />

                {/* Viking medallion ornament. Same component + variant
                    + ornament combo the Arcane Trials coin buttons use,
                    so it gets the project's shared cache-bust treatment
                    (CEF/Chromium both aggressively cache CSS mask-image
                    URLs).

                    The SVG's decorative content is inset from its own
                    viewBox edges (outer ring at ~80% of the viewBox
                    diameter) so the default mask-size: contain renders
                    the ornament smaller than the button. Overriding to
                    ~128% scales the mask up until the content reaches
                    the container edges. */}
                <VikingOrnament
                  ornament="style2d007"
                  variant="medallion"
                  color="rgba(204, 162, 74, 1)"
                  opacity={0.62}
                  className="avatar-medallion-fire-shimmer"
                  style={{
                    maskSize: '128%',
                    WebkitMaskSize: '128%',
                    // Layer stone + scratch textures into the medallion
                    // background so the knotwork reads as weathered cast
                    // metal rather than a flat tint. The mask restricts
                    // everything to the ornament's strokes, so the
                    // textures only appear where the knotwork is. The
                    // live hue/brightness shimmer applies to this
                    // composite, so the textured surface glows uniformly
                    // when the metal heats up. Stack (top → bottom):
                    //   1. scratches. Overlay blend, subtle wear marks
                    //   2. stone. Soft-light, adds aged depth
                    //   3. radial. The original gold fade
                    backgroundImage: [
                      "url('/tex-scratches.webp')",
                      "url('/tex-stone.webp')",
                      'radial-gradient(circle at center, rgba(204, 162, 74, 1), transparent 100%)',
                    ].join(', '),
                    backgroundBlendMode: 'overlay, soft-light, normal',
                    backgroundSize: '220px, 260px, 100% 100%',
                    backgroundRepeat: 'repeat, repeat, no-repeat',
                    backgroundPosition: 'center, center, center',
                  }}
                />

                {/* XP ring. Level progress lives here as a golden arc
                    hugging the medallion rim (there is no XP bar row;
                    the medallion is the single progression object).
                    pathLength=100 keeps the dash math in percent. The
                    wide low-alpha stroke underneath fakes a glow
                    without a CSS filter (see AchievementMedallion for
                    why filters are avoided: each one forces its own
                    compositor layer). */}
                {(() => {
                  const ringPct = progress.isMax ? 100 : Math.round(progress.fraction * 100);
                  const ringOffset = 100 - ringPct;
                  return (
                    <svg
                      viewBox="0 0 100 100"
                      className="absolute inset-0 pointer-events-none"
                      style={{ zIndex: 1, transform: 'rotate(-90deg)' }}
                      role="img"
                      aria-label={progress.isMax ? t('Level progress: max level') : t('Level progress: {pct}%', { pct: ringPct })}
                    >
                      <defs>
                        <linearGradient id="hero-xp-ring" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#8b6914" />
                          <stop offset="55%" stopColor="#d4a843" />
                          <stop offset="100%" stopColor="#f4d788" />
                        </linearGradient>
                      </defs>
                      {/* Recessed track: dark groove + faint gold tint */}
                      <circle cx="50" cy="50" r="48.2" fill="none" stroke="rgba(0,0,0,0.55)" strokeWidth="2.8" />
                      <circle cx="50" cy="50" r="48.2" fill="none" stroke={`${GOLD} 0.16)`} strokeWidth="2.2" />
                      {ringPct > 0 && (
                        <>
                          <circle
                            className="hero-ring-in"
                            cx="50" cy="50" r="48.2" fill="none"
                            stroke="url(#hero-xp-ring)" strokeWidth="4.5" strokeLinecap="round"
                            pathLength="100" strokeDasharray="100" strokeDashoffset={ringOffset}
                            opacity="0.3" style={{ animationDelay: '250ms' }}
                          />
                          <circle
                            className="hero-ring-in"
                            cx="50" cy="50" r="48.2" fill="none"
                            stroke="url(#hero-xp-ring)" strokeWidth="2.2" strokeLinecap="round"
                            pathLength="100" strokeDasharray="100" strokeDashoffset={ringOffset}
                            style={{ animationDelay: '250ms' }}
                          />
                        </>
                      )}
                    </svg>
                  );
                })()}

                {/* Profile picture. Centered, sized so ~12px of the
                    medallion ring shows around the rim. Border is fully
                    opaque gold so it reads as a clean metal frame
                    seating the portrait against the medallion. */}
                {avatarUrl ? (
                  <div
                    className="absolute rounded-full overflow-hidden"
                    style={{
                      width: '7cqw', height: '7cqw',
                      top: '0.875cqw', left: '0.875cqw',
                      border: `1.5px solid ${GOLD} 1)`,
                      boxShadow: `inset 0 0 0 1px rgba(0,0,0,0.5), 0 3px 12px rgba(0,0,0,0.65)`,
                      zIndex: 2,
                    }}
                  >
                    <AvatarImage src={avatarUrl} alt={t('Avatar')} className="w-full h-full" />
                  </div>
                ) : (
                  <div
                    className="absolute rounded-full flex items-center justify-center text-2xl"
                    style={{
                      width: '7cqw', height: '7cqw',
                      top: '0.875cqw', left: '0.875cqw',
                      background: `${GOLD} 0.08)`,
                      border: `1.5px solid ${GOLD} 1)`,
                      color: `${GOLD} 0.5)`,
                      zIndex: 2,
                    }}
                  >?</div>
                )}

                {/* Level badge. Now a button that opens the
                    Progression screen. A gold dot in the corner
                    surfaces "you have unclaimed rewards" permanently
                    (the catch-all for the bug class where transient
                    popups could be missed). */}
                {(() => {
                  const pending = hasAnyPendingClaim(profile);
                  const { onOpenProgression } = this.props;
                  // Highlight surfaces stay fully opaque solids (no
                  // transparency on this element); only the box-shadow
                  // glow carries alpha, which is allowed for shadows.
                  const baseBg = 'linear-gradient(180deg, rgba(60,48,28,1) 0%, rgba(28,22,12,1) 100%)';
                  const hoverBg = 'linear-gradient(180deg, rgba(92,74,42,1) 0%, rgba(52,40,22,1) 100%)';
                  const pressBg = 'linear-gradient(180deg, rgba(40,32,18,1) 0%, rgba(22,17,9,1) 100%)';
                  const baseBorder = `2px solid ${GOLD} 0.85)`;
                  const hoverBorder = `2px solid ${ACCENT_GOLD}`;
                  const baseShadow = `0 3px 10px rgba(0,0,0,0.75), inset 0 1px 0 ${GOLD} 0.2)`;
                  const hoverShadow = `0 4px 14px rgba(0,0,0,0.8), 0 0 10px ${GOLD} 0.55), inset 0 1px 0 ${GOLD} 0.35)`;
                  const pressShadow = `0 1px 4px rgba(0,0,0,0.7), inset 0 2px 5px rgba(0,0,0,0.55)`;
                  const applyBase = (el) => { el.style.background = baseBg; el.style.border = baseBorder; el.style.boxShadow = baseShadow; el.style.transform = 'none'; };
                  const applyHover = (el) => { el.style.background = hoverBg; el.style.border = hoverBorder; el.style.boxShadow = hoverShadow; el.style.transform = 'none'; };
                  const applyPress = (el) => { el.style.background = pressBg; el.style.border = hoverBorder; el.style.boxShadow = pressShadow; el.style.transform = 'scale(0.96)'; };
                  // The chip is nested inside the avatar's `group` button,
                  // so hovering it also triggers the profile "View Profile"
                  // scrim via group-hover. Suppress that scrim while the
                  // cursor is on the chip; clearing the overrides lets
                  // group-hover resume once the cursor moves onto the avatar.
                  const setProfileScrimHidden = (el, hidden) => {
                    const overlay = el.closest('.group')?.querySelector('[data-profile-hover-overlay]');
                    if (!overlay) return;
                    overlay.style.backgroundColor = hidden ? 'transparent' : '';
                    const hint = overlay.querySelector('[data-profile-hover-hint]');
                    if (hint) hint.style.opacity = hidden ? '0' : '';
                  };
                  return (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenProgression?.();
                      }}
                      onMouseEnter={(e) => { applyHover(e.currentTarget); setProfileScrimHidden(e.currentTarget, true); }}
                      onMouseLeave={(e) => { applyBase(e.currentTarget); setProfileScrimHidden(e.currentTarget, false); }}
                      onMouseDown={(e) => applyPress(e.currentTarget)}
                      onMouseUp={(e) => applyHover(e.currentTarget)}
                      className="absolute left-1/2 -translate-x-1/2 z-20 flex items-center justify-center"
                      style={{
                        bottom: 2,
                        minWidth: '2.375cqw', height: '1.75cqw',
                        padding: '0 0.625cqw',
                        background: baseBg,
                        border: baseBorder,
                        borderRadius: 'var(--radius-xl)',
                        boxShadow: baseShadow,
                        transition: 'background 120ms ease, border-color 120ms ease, box-shadow 120ms ease, transform 90ms ease',
                      }}
                      aria-label={pending ? t('Open progression. Rewards waiting') : t('Open progression')}
                    >
                      <span className="text-sm font-bold tabular-nums" style={{ color: ACCENT_GOLD, textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>{level}</span>
                      {pending ? (
                        <span
                          aria-hidden
                          style={{
                            position: 'absolute',
                            top: '-0.25cqw',
                            right: '-0.25cqw',
                            width: '0.7cqw',
                            height: '0.7cqw',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle at 35% 30%, #fbe9b8 0%, #e8c466 55%, #b9893a 100%)',
                            boxShadow: `0 0 8px ${GOLD} 0.85), inset 0 1px 0 rgba(255,255,255,0.5)`,
                            border: '1px solid rgba(0,0,0,0.4)',
                          }}
                        />
                      ) : null}
                    </button>
                  );
                })()}

                {/* Hover overlay. Matches the profile picture bounds
                    so the "View Profile" hint sits on the avatar itself,
                    not on the medallion ring. */}
                <div
                  data-profile-hover-overlay
                  className="absolute rounded-full bg-black/0 group-hover:bg-black/55 flex items-center justify-center transition-colors"
                  style={{
                    width: '7cqw', height: '7cqw',
                    top: '0.875cqw', left: '0.875cqw',
                    zIndex: 3,
                  }}
                >
                  <span data-profile-hover-hint className="text-white/90 opacity-0 group-hover:opacity-100 text-xs font-medium transition-opacity text-center px-2" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{t('View Profile')}</span>
                </div>
              </button>

              {/* Content column. Name + rank, progress bars, collection bars */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h1
                    className="text-3xl font-bold arena-heading cursor-pointer transition-colors"
                    style={{
                      color: '#f4e4b8',
                      textShadow: '0 2px 4px rgba(0,0,0,0.6), 0 0 24px rgba(200,160,60,0.18)',
                      letterSpacing: '-0.01em',
                    }}
                    onClick={() => onViewProfile?.(profile.id)}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#ffe9a8'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#f4e4b8'; }}
                    title={t('View your profile')}
                  >
                    {profile.name}{this.state.selfHasTitle ? ',' : ''}
                  </h1>
                  {rank && (() => {
                    const tierHex = TIER_HEX[rank.tier] || '#A6A09B';
                    // Etched plate, not a translucent pill: near-opaque
                    // forged base (same family as GLASS_PANEL_STYLE), bevel via
                    // inset highlights, tier color confined to the
                    // engraving and a faint halo.
                    return (
                      <span
                        className="inline-flex items-center shrink-0"
                        style={{
                          padding: '0.3cqw 0.75cqw',
                          borderRadius: PANEL_CONTROL_RADIUS,
                          background: 'linear-gradient(180deg, rgba(26,21,12,0.96) 0%, rgba(12,10,8,0.97) 100%)',
                          border: `1px solid ${tierHex}55`,
                          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.07), inset 0 -1px 0 rgba(0,0,0,0.65), 0 2px 8px rgba(0,0,0,0.5), 0 0 12px ${tierHex}1f`,
                        }}
                      >
                        <span style={{ ...HUD_LABEL_STYLE, fontSize: '0.66cqw', color: tierHex, textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                          {formatRank(rank.tier, rank.division)}
                        </span>
                      </span>
                    );
                  })()}
                </div>

                {/* Crown title row. Sits just under the H1 name so the
                    byline reads as "{Name},\n{Title}." Updates live via
                    PlayerTitle's onResolve callback so a change in the
                    Edit Profile picker (or a crown lost / won) is
                    reflected immediately. */}
                <PlayerTitle
                  player={profile}
                  sorceryCards={this.props.sorceryCards}
                  prefix=""
                  className="block mb-2"
                  style={{
                    fontSize: '0.78cqw',
                    lineHeight: 1.1,
                    color: '#f4e4b8',
                    marginTop: '-0.4cqw',
                  }}
                  onResolve={(has) => {
                    if (has !== this.state.selfHasTitle) {
                      this.setState({ selfHasTitle: has });
                    }
                  }}
                />

                {/* Ranked LP bar. The ONLY full-width bar in the hero:
                    XP lives on the medallion ring, collection is a
                    quiet footer, so the competitive stat owns the row.
                    Division notches at 25/50/75 make progress read
                    spatially instead of as a naked pill. */}
                {rank && (() => {
                  const tierHex = TIER_HEX[rank.tier] || '#A6A09B';
                  const lp = Math.min(100, Math.max(0, rank.lp || 0));
                  return (
                    <div className="flex items-center gap-3 mb-3">
                      <span className="shrink-0 whitespace-nowrap" style={{ ...HUD_LABEL_STYLE, color: `${tierHex}cc`, width: '4.5cqw' }}>{t('Ranked LP')}</span>
                      {/* Concentric with the 0.5cqw panel: trough at the
                          0.375cqw control radius, fill stepped in past
                          the trough's 1px border. */}
                      <div className="flex-1 h-4 overflow-hidden relative" style={{ borderRadius: PANEL_CONTROL_RADIUS, background: 'rgba(0,0,0,0.55)', border: `1px solid ${tierHex}3a`, boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6), inset 0 -1px 0 rgba(255,255,255,0.04)' }}>
                        <div
                          className="hero-fill-in h-full transition-[width]"
                          style={{
                            width: `${lp}%`,
                            borderRadius: `calc(${PANEL_CONTROL_RADIUS} - 1px)`,
                            animationDelay: '300ms',
                            background: `linear-gradient(90deg, ${tierHex}88 0%, ${tierHex} 100%)`,
                            boxShadow: `0 0 10px ${tierHex}77, inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.25)`,
                          }}
                        />
                        {[25, 50, 75].map((x) => (
                          <div
                            key={x}
                            aria-hidden="true"
                            className="absolute inset-y-0"
                            style={{ left: `${x}%`, width: '1.5px', background: 'rgba(0,0,0,0.6)', boxShadow: '1px 0 0 rgba(255,255,255,0.05)' }}
                          />
                        ))}
                      </div>
                      <span className="tabular-nums font-bold shrink-0 w-16 text-right" style={{ fontSize: '0.6875cqw', color: `${tierHex}cc`, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{t('{lp} / 100 LP', { lp: rank.lp || 0 })}</span>
                    </div>
                  );
                })()}

                {/* Collection footer. Counts only, hue confined to the
                    gem dots so the panel stays gold + tier color. The
                    per-set progress bars live on the Collection screen
                    where they're actionable. Mirrors the LP row's
                    label | content | value rails: same 4.5cqw label
                    column, set entries distributed evenly across the
                    bar's span, and the grand total right-aligned in
                    the same w-16 value column. */}
                <div className="flex items-center gap-3">
                  <span className="shrink-0 whitespace-nowrap" style={{ ...HUD_LABEL_STYLE, color: `${GOLD} 0.55)`, width: '4.5cqw' }}>{t('Collection')}</span>
                  <div className="flex items-center flex-1 min-w-0">
                    {[
                      { label: 'Beta', key: 'beta' },
                      { label: 'Arthurian', key: 'arthurian' },
                      { label: 'Gothic', key: 'gothic' },
                    ].map(({ label, key }) => (
                      <div key={key} className="flex items-center flex-1 min-w-0" style={{ gap: '0.4cqw' }}>
                        <span style={{ fontSize: '0.625cqw', fontWeight: 600, color: 'rgba(232,220,195,0.6)' }}>{label}</span>
                        <span className="tabular-nums" style={{ fontSize: '0.625cqw', fontWeight: 600, color: 'rgba(232,220,195,0.8)' }}>{setStats[key].owned}/{setStats[key].total}</span>
                      </div>
                    ))}
                  </div>
                  <span className="tabular-nums shrink-0 w-16 text-right" style={{ fontSize: '0.625cqw', fontWeight: 600, color: 'rgba(232,220,195,0.55)' }}>
                    {setStats.beta.owned + setStats.arthurian.owned + setStats.gothic.owned} / {setStats.beta.total + setStats.arthurian.total + setStats.gothic.total}
                  </span>
                </div>
              </div>

              {/* Stat tiles. Substantial vertical column anchoring the right
                  side of the hero, matching the visual weight of the stacked
                  bars in the content column. */}
              {/* Uniform 0.75cqw frame: the gap between the two tiles
                  equals their distance from the panel's top, right, and
                  bottom edges. The panel pads content at 1.25cqw/1.5cqw,
                  so negative margins pull the column out to the 0.75cqw
                  ring and self-stretch sizes the tiles from the
                  reclaimed space. */}
              <div className="flex flex-col shrink-0 self-stretch" style={{ width: '7.25cqw', gap: '0.75cqw', margin: '-0.5cqw -0.75cqw -0.5cqw 0' }}>
                {(() => {
                  // A win rate is undefined (not zero) with no matches
                  // played, so the tile shows a neutral dash placeholder
                  // and skips the green treatment.
                  const hasMatches = totalMatches > 0;
                  const winning = hasMatches && winRate >= 50;
                  const tileRule = (color) => (
                    <span
                      aria-hidden="true"
                      style={{
                        width: '2.5cqw', height: '1px',
                        margin: '0.3cqw 0 0.4cqw',
                        background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
                      }}
                    />
                  );
                  const tileStyle = (green) => ({
                    borderRadius: PANEL_CONTROL_RADIUS,
                    background: green
                      ? 'linear-gradient(180deg, rgba(18,40,26,0.78) 0%, rgba(10,22,15,0.85) 100%)'
                      : 'linear-gradient(180deg, rgba(20,15,8,0.72) 0%, rgba(10,7,4,0.82) 100%)',
                    border: `1px solid ${green ? 'rgba(110,231,160,0.4)' : `${GOLD} 0.3)`}`,
                    boxShadow: green
                      ? 'inset 0 1px 0 rgba(110,231,160,0.14), 0 3px 10px rgba(0,0,0,0.45)'
                      : 'inset 0 1px 0 rgba(255,220,140,0.12), 0 3px 10px rgba(0,0,0,0.45)',
                  });
                  return (
                    <>
                      <div className="flex-1 flex flex-col items-center justify-center px-3" style={tileStyle(false)}>
                        <span style={{ ...HUD_LABEL_STYLE, color: `${GOLD} 0.7)` }}>{t('Matches')}</span>
                        {tileRule(`${GOLD} 0.45)`)}
                        <span className="text-4xl font-bold tabular-nums leading-none" style={{ color: '#f4e4b8', textShadow: '0 2px 6px rgba(0,0,0,0.75), 0 0 18px rgba(200,160,60,0.15)' }}>
                          <CountUpNumber value={totalMatches} />
                        </span>
                      </div>
                      <div className="flex-1 flex flex-col items-center justify-center px-3" style={tileStyle(winning)}>
                        <span style={{ ...HUD_LABEL_STYLE, color: winning ? 'rgba(110,231,160,0.75)' : `${GOLD} 0.7)` }}>{t('Win Rate')}</span>
                        {tileRule(winning ? 'rgba(110,231,160,0.5)' : `${GOLD} 0.45)`)}
                        {hasMatches ? (
                          <span className="text-4xl font-bold tabular-nums leading-none" style={{ color: winning ? '#6ee7a0' : '#f4e4b8', textShadow: '0 2px 6px rgba(0,0,0,0.75), 0 0 18px rgba(200,160,60,0.15)' }}>
                            <CountUpNumber value={winRate} />%
                          </span>
                        ) : (
                          <span className="text-4xl font-bold leading-none" style={{ color: 'rgba(244,228,184,0.45)', textShadow: '0 2px 6px rgba(0,0,0,0.75)' }}>{EM_DASH}</span>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* my-4 gives equal vertical breathing room above and
                below the divider so it sits centered between the hero
                panel and the leaderboard panel. */}
            <OrnamentalDivider className="hub-panel-enter hub-enter-2 shrink-0 my-4" />

              {/* ── LEADERBOARD ── sits directly under the hero inside
                  the center column wrapper opened above. Achievements
                  now live as a full-height sibling of this center
                  column (see the closing tag after the leaderboard),
                  so the hero is centred only over the leaderboard. */}
              <div className="flex-1 min-w-0 flex flex-col min-h-0">
                <div
                  data-tutorial="leaderboard"
                  className="hub-glass-panel hub-panel-enter hub-enter-3 relative flex-1 flex flex-col min-h-0"
                  style={GLASS_PANEL_STYLE}
                >
                  <GlassSurface />
                  <CornerPlating position="top-left" color={`${GOLD} 0.6)`} />
                  <CornerPlating position="top-right" color={`${GOLD} 0.6)`} />
                  <CornerPlating position="bottom-left" color={`${GOLD} 0.6)`} />
                  <CornerPlating position="bottom-right" color={`${GOLD} 0.6)`} />
                  {/* Hall header. Two rows on every tab so the panel never
                      reflows when switching: row one is the tabs and the
                      looking-for-a-match widget, row two the active tab's
                      filters, always the same height. */}
                  <div className="shrink-0 flex flex-col" style={{ padding: HALL_PAD, gap: HALL_PAD }}>
                    <div className="flex items-center justify-between gap-3">
                      <PillTabs
                        tabs={HALL_TABS}
                        value={hallTab}
                        minCol="7cqw"
                        height={HALL_WIDGET_HEIGHT}
                        radius={HALL_RADIUS}
                        onChange={this.setHallTab}
                        renderLabel={(tab) => {
                          const count = tab.value === 'players' ? this.state.onlinePlayers.length : leaderboardTotal;
                          return (
                            <>
                              {t(tab.label)}
                              {count > 0 ? <span className="tabular-nums" style={{ marginLeft: 6, opacity: 0.7, fontWeight: 500 }}>{count.toLocaleString()}</span> : null}
                            </>
                          );
                        }}
                      />
                      {/* Season and Hall of Fame open their own screens, so
                          they live up here on every tab rather than in the
                          Leaderboard tab's filter row, which is already full
                          with the mode tabs, search, tier and Find me. Sized
                          to the widget's visible box (its bevel rings take
                          4px on every side). */}
                      <div className="flex items-center shrink-0" style={{ gap: HALL_PAD }}>
                        <HeaderPill
                          radius={HALL_RADIUS}
                          height={`calc(${HALL_WIDGET_HEIGHT} - 8px)`}
                          onClick={() => { playUI(UI.CLICK); this.props.onOpenSeasonRanking?.(); }}
                          hint={t('Bi-weekly Ranked Season standings. Top 3 earn rewards.')}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.875cqw', height: '0.875cqw' }} aria-hidden="true">
                            <path d="M12 2l2.6 5.27 5.82.85-4.21 4.1.99 5.78L12 15.27l-5.2 2.73.99-5.78L3.58 8.12l5.82-.85L12 2z" />
                          </svg>
                          <span>{t('Season')}</span>
                        </HeaderPill>
                        <HeaderPill
                          radius={HALL_RADIUS}
                          height={`calc(${HALL_WIDGET_HEIGHT} - 8px)`}
                          onClick={() => { playUI(UI.CLICK); this.props.onOpenHallOfFame?.(); }}
                          hint={t('Win rates by avatar and element')}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.875cqw', height: '0.875cqw' }} aria-hidden="true">
                            <path d="M6 9H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2" />
                            <path d="M18 9h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
                            <path d="M6 3h12v6a6 6 0 0 1-12 0V3z" />
                            <path d="M9 21h6" />
                            <path d="M12 17v4" />
                          </svg>
                          <span>{t('Hall of Fame')}</span>
                        </HeaderPill>
                        <LfgWidget lfg={this.state.lfg} width="18cqw" height={HALL_WIDGET_HEIGHT} radius={HALL_RADIUS} onClick={() => { playUI(UI.OPEN); this.props.onOpenLfg?.(); }} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2" style={{ height: ROW_HEIGHT_CQW }}>
                      {hallTab === 'players' ? (
                        <>
                          <PillTabs tabs={PLAYER_FILTERS.map((f) => ({ ...f, label: t(f.label) }))} value={this.state.playersFilter} minCol="5.5cqw" radius={HALL_RADIUS} onChange={(v) => this.setState({ playersFilter: v })} />
                          <div className="flex-1 min-w-0">
                            <SearchWell value={this.state.playersSearch} onInput={(v) => this.setState({ playersSearch: v })} width="100%" radius={HALL_RADIUS} placeholder={t('Search players…')} />
                          </div>
                        </>
                      ) : (
                        <>
                          <PillTabs
                            tabs={[{ value: 'ranked', label: t('Ranked') }, { value: 'limited', label: t('Limited') }, { value: 'sandbox', label: t('Sandbox') }]}
                            value={leaderboardMode}
                            radius={HALL_RADIUS}
                            onChange={this.setLeaderboardMode}
                          />
                          <div className="flex-1 min-w-0">
                            <SearchWell value={leaderboardSearch} onInput={this.setLeaderboardSearch} width="100%" radius={HALL_RADIUS} placeholder={t('Search the ladder…')} />
                          </div>
                          {leaderboardMode === 'ranked' ? (
                            <Select
                              ariaLabel={t('Filter by tier')}
                              options={[{ value: 'all', label: t('All tiers') }, ...TIERS.map((tier) => ({ value: tier, label: t(TIER_LABELS[tier]) }))]}
                              value={leaderboardFilter}
                              onValueChange={(v) => { playUI(UI.CLICK); this.setLeaderboardFilter(v); }}
                              portalMenu
                              menuPreferredWidth={160}
                              triggerStyle={{ height: ROW_HEIGHT_CQW, width: '8.5cqw', borderRadius: HALL_RADIUS }}
                            />
                          ) : null}
                          <HeaderPill
                            radius={HALL_RADIUS}
                            disabled={!selfRank?.rank}
                            onClick={() => { playUI(UI.CLICK); this.jumpToSelfRank(); }}
                            hint={selfRank?.rank ? t('You are #{rank} of {total}', { rank: selfRank.rank.toLocaleString(), total: selfRank.total.toLocaleString() }) : null}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.875cqw', height: '0.875cqw' }} aria-hidden="true">
                              <circle cx="12" cy="12" r="9" />
                              <circle cx="12" cy="12" r="4" />
                              <circle cx="12" cy="12" r="1" fill="currentColor" />
                            </svg>
                            <span>{t('Find me')}</span>
                          </HeaderPill>
                        </>
                      )}
                    </div>
                  </div>
 {/* Divider */}
 <div className="h-px" style={{ margin: `0 ${HALL_PAD}`, background: `linear-gradient(90deg, transparent, ${GOLD} 0.2), transparent)` }} />
                  {/* Player list. Virtualized so only the rows in the
                      visible viewport (+ overscan) are mounted. Scrolling
                      a 1000-entry list now only re-renders ~12 rows per
                      frame instead of all 1000, which keeps the scroll
                      engine in sync with the user's input gesture
                      (otherwise wheel deltas queue up faster than rows
                      can paint, audible as the scroll-sfx looping). */}
                  {hallTab === 'players' ? (() => {
                    const { onlinePlayers, onlineLoading, playersSearch, playersFilter, friendRequestsSent } = this.state;
                    const friendIds = new Set((friendListData?.friends || []).map((f) => f.id));
                    const q = playersSearch.trim().toLowerCase();
                    const list = onlinePlayers
                      .filter((p) => !q || (p.name || '').toLowerCase().includes(q))
                      .filter((p) => playersFilter === 'all' || (playersFilter === 'friends' ? friendIds.has(p.id) : playersFilter === 'looking' ? !!p.lfg : isInMatch(p)))
                      .sort((a, b) => activityRank(a) - activityRank(b) || (a.name || '').localeCompare(b.name || ''));
                    if (onlineLoading && onlinePlayers.length === 0) {
                      return (
                        <div className="flex-1 overflow-hidden" style={{ padding: HALL_PAD }}>
                          {Array.from({ length: 8 }).map((_, i) => (
                            <div key={`pl-skel-${i}`} style={{ height: '3.25cqw' }}><LeaderboardSkeletonRow variant="pip" rank={null} /></div>
                          ))}
                        </div>
                      );
                    }
                    if (list.length === 0) {
                      return (
                        <div className="flex-1 flex items-center justify-center text-xs px-6 text-center" style={{ color: `${GOLD} 0.5)` }}>
                          {onlinePlayers.length === 0 ? t('Nobody else is online right now. Host a table and it waits for the next player to arrive.') : t('No players match.')}
                        </div>
                      );
                    }
                    return (
                      <div className="flex-1 min-h-0 overflow-y-auto" style={{ padding: HALL_PAD }}>
                        {list.map((p) => (
                          <HallPlayerRow
                            key={p.id}
                            player={p}
                            isMe={p.id === profile.id}
                            isFriend={friendIds.has(p.id)}
                            requestSent={friendRequestsSent.has(p.id)}
                            sorceryCards={sorceryCards}
                            onView={onViewProfile}
                            onChallenge={this.props.onChallenge}
                            onChat={(pl) => this.props.onOpenChat?.(pl.id)}
                            onAddFriend={this.handleAddFriend}
                          />
                        ))}
                      </div>
                    );
                  })() : null}
                  {hallTab !== 'leaderboard' ? null : leaderboardInitialLoading ? (
                    // Skeleton stack at the same row height the real

                    // VirtualList will use, so the panel doesn't reflow
                    // when data arrives. 14 rows is enough to fill the
                    // panel at any reasonable canvas size; extras simply
                    // overflow under `overflow-hidden`.
                    <div className="flex-1 overflow-hidden" style={{ padding: HALL_PAD }}>
                      {Array.from({ length: 14 }).map((_, i) => (
                        <div
                          key={`init-skel-${i}`}
                          style={{ height: `${Math.round(LEADERBOARD_ROW_PX * this.state.canvasScale)}px` }}
                        >
                          <LeaderboardSkeletonRow variant="pip" rank={null} />
                        </div>
                      ))}
                    </div>
                  ) : leaderboardTotal === 0 ? (
                    <div className="flex-1 flex items-center justify-center text-xs" style={{ color: `${GOLD} 0.5)` }}>{t('No players found')}</div>
                  ) : (
                    <VirtualList
                      ref={this._leaderboardRef}
                      className="flex-1"
                      style={{ padding: HALL_PAD }}
                      items={leaderboardRows.length >= leaderboardTotal ? leaderboardRows : (() => {
                        // Ensure the sparse array reports `total` length
                        // so VirtualList allocates the full scroll height
                        // even before later pages have loaded.
                        const padded = leaderboardRows.slice();
                        padded.length = leaderboardTotal;
                        return padded;
                      })()}
                      itemHeight={Math.round(LEADERBOARD_ROW_PX * this.state.canvasScale)}
                      overscan={6}
                      onVisibleRangeChange={this.handleLeaderboardVisibleRange}
                      keyFor={(p, i) => (p && p.id) || `placeholder-${i}`}
                      renderItem={(player, i) => {
                        const rank = i + 1;
                        if (!player) {
                          return <LeaderboardSkeletonRow rank={rank} variant="rank" />;
                        }
                        const isMe = player.name === profile.name;
                        const tierHex = TIER_HEX[player.tier] || '#A6A09B';
                        const isRankedView = leaderboardMode === 'ranked';
                        const modeWins = Number(player.modeWins ?? 0);
                        const modeLosses = Number(player.modeLosses ?? 0);
                        const canChallenge = !!player.online && !isMe && !!this.props.onChallenge;
                        return (
                          <div className="relative group h-full">
                          <button
                            type="button"
                            className="relative flex items-center gap-3 pl-0 pr-3 py-2.5 transition-[background,border-color] cursor-pointer w-full text-left h-full"
                            style={{
                              borderRadius: HALL_RADIUS,
                              isolation: 'isolate',
                              background: isMe
                                ? `linear-gradient(90deg, rgba(212,168,67,0.22) 0%, rgba(212,168,67,0.08) 45%, transparent 90%)`
                                : 'transparent',
                              border: isMe
                                ? `1px solid ${GOLD} 0.35)`
                                : '1px solid transparent',
                            }}
                            onMouseEnter={(e) => { if (!isMe) e.currentTarget.style.background = `linear-gradient(90deg, rgba(166,160,155,0.08) 0%, transparent 80%)`; }}
                            onMouseLeave={(e) => { if (!isMe) e.currentTarget.style.background = 'transparent'; }}
                            onClick={() => player.id && onViewProfile(player.id)}
                          >
                              <RowArtBackdrop player={player} sorceryCards={sorceryCards} borderRadius={HALL_RADIUS} />
                              <RankNumeral rank={rank} />
                              <div className="relative shrink-0">
                                <RowAvatar player={player} sorceryCards={sorceryCards} />
                                {player.online ? (
                                  <span
                                    className="absolute rounded-full"
                                    style={{ right: '-0.1cqw', bottom: '-0.1cqw', width: '0.6cqw', height: '0.6cqw', background: ONLINE_DOT, border: '1px solid #1a140a', boxShadow: `0 0 5px ${ONLINE_DOT}` }}
                                  />
                                ) : null}
                              </div>
                              <div className="flex-1 min-w-0 flex items-center gap-2">
                                <div className="min-w-0 flex flex-col">
                                  <span
                                    className="font-semibold truncate text-[0.8125cqw] min-w-0"
                                    style={{ color: isMe ? ROW_TEXT_SELF : ROW_TEXT, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                                  >
                                    {player.name}{this.state.leaderboardIdsWithTitle.has(player.id) ? ',' : ''}
                                  </span>
                                  <PlayerTitle
                                    player={player}
                                    sorceryCards={sorceryCards}
                                    prefix=""
                                    style={{
                                      display: 'block',
                                      fontSize: '0.6cqw',
                                      lineHeight: 1.1,
                                      whiteSpace: 'nowrap',
                                      maxWidth: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      marginTop: '-0.2cqw',
                                      color: isMe ? ROW_TEXT_SELF : ROW_TEXT,
                                    }}
                                    onResolve={(has) => {
                                      this.setState((s) => {
                                        const had = s.leaderboardIdsWithTitle.has(player.id);
                                        if (had === has) return null;
                                        const next = new Set(s.leaderboardIdsWithTitle);
                                        if (has) next.add(player.id); else next.delete(player.id);
                                        return { leaderboardIdsWithTitle: next };
                                      });
                                    }}
                                  />
                                </div>
                                {/* Badges the player pinned to their
                                    profile, sitting right of the name so
                                    the ladder shows the same emblems the
                                    profile header does. */}
                                <PlayerBadgeStrip
                                  player={player}
                                  sorceryCards={sorceryCards}
                                  size="1.5cqw"
                                />
                                {player.discordHandle ? (
                                  <DiscordHandlePill
                                    handle={player.discordHandle}
                                    size="sm"
                                    withAt
                                    className="shrink-0"
                                  />
                                ) : null}
                              </div>
                              {isRankedView ? (
                                <span
                                  className={`shrink-0 text-right text-[0.625cqw] font-bold${canChallenge ? ' group-hover:opacity-0 transition-opacity' : ''}`}
                                  style={{ color: tierHex, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
                                >
                                  {formatRank(player.tier, player.division)}
                                </span>
                              ) : (
                                <Tooltip content={t('{wins} wins / {losses} losses', { wins: modeWins.toLocaleString(), losses: modeLosses.toLocaleString() })} side="top" delay={350}>
                                  <span
                                    className="shrink-0 text-right text-[0.625cqw] font-bold tabular-nums"
                                    style={{ color: ACCENT_GOLD, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
                                  >
                                    {t('{wins}W · {losses}L', { wins: modeWins.toLocaleString(), losses: modeLosses.toLocaleString() })}
                                  </span>
                                </Tooltip>
                              )}
                            </button>
                            {canChallenge ? (
                              <LadderChallenge player={player} onChallenge={this.props.onChallenge} />
                            ) : null}
                          </div>
                          );
                        }}
                      />
                    )}
                </div>
              </div>

            </div>
            {/* ── end LEFT GRID CELL ── */}

            {/* ── RIGHT GRID CELL: GLOBAL CHAT (full-height) ──
                Sibling of the left cell inside the right-side grid,
                so it shares the same parent as the leaderboard and
                the two are laid out side-by-side by grid-template
                -columns. */}
            <div className="flex flex-col min-h-0" data-tutorial="tavern">
                <div
                  className="hub-glass-panel hub-panel-enter hub-enter-4 relative flex-1 flex flex-col min-h-0"
                  style={GLASS_PANEL_STYLE}
                >
                  <GlassSurface />
                  <CornerPlating position="top-left" color={`${GOLD} 0.6)`} />
                  <CornerPlating position="top-right" color={`${GOLD} 0.6)`} />
                  <CornerPlating position="bottom-left" color={`${GOLD} 0.6)`} />
                  <CornerPlating position="bottom-right" color={`${GOLD} 0.6)`} />
                  <GlobalChatView
                    profile={profile}
                    sorceryCards={sorceryCards}
                    friends={friendListData?.friends}
                    onViewProfile={onViewProfile}
                    canModerate={this.props.canModerateChat}
                    onWatchReplay={this.props.onWatchReplay}
                    onOpenCard={(card) => { playUI(UI.INSPECTOR_OPEN); this.setState({ inspectedCard: card }); }}
                  />
                </div>
              </div>

            </div>
          </div>
        </div>

        {this.state.inspectedCard ? (
          <CardInspector
            card={this.state.inspectedCard}
            imageUrl={this.state.inspectedCard.printings?.[0]?.image_url}
            rarity={this.state.inspectedCard.rarity}
            foiling={this.state.inspectedCard.printings?.[0]?.foiling}
            onClose={() => { playUI(UI.INSPECTOR_CLOSE); this.setState({ inspectedCard: null }); }}
          />
        ) : null}

        {/* ─── ONBOARDING TUTORIAL ──────────────────────────── */}
        {this.state.showHubTutorial && (
          <TutorialOverlay
            steps={HUB_TUTORIAL_STEPS.map((s) => ({ ...s, title: t(s.title), body: t(s.body) }))}
            onDismiss={this.handleHubTutorialDismiss}
          />
        )}

        {/* ─── ADMIN PANEL ───────────────────────────────────── */}
        {this.state.showAdmin && this.props.isAdmin && (
          <AdminPanel
            profile={this.props.profile}
            sorceryCards={this.props.sorceryCards}
            onUpdateProfile={(updated) => {
              this.props.onUpdateProfile?.(updated);
            }}
            onClose={() => this.setState({ showAdmin: false })}
          />
        )}

        {this.state.showCredits && (
          <CreditsScreen onClose={() => this.setState({ showCredits: false })} />
        )}

        {this.state.showChangelog && (
          <ChangelogModal
            version={this.state.changelogData?.version || updateStatus?.currentVersion}
            notes={this.state.changelogData?.notes}
            loading={this.state.changelogLoading}
            onClose={this.closeChangelog}
          />
        )}

      </div>
    );
  }
}
